import React, { useEffect, useState } from 'react';
import { TrendingUp, Loader2 } from 'lucide-react';

/**
 * Recently-sold comparables from official sources (IE PPR / UK HM Land
 * Registry). Renders below the property-detail map so buyers can see
 * what similar homes actually sold for in the same postal geography.
 *
 * Matched by:
 *   • UK  → outward postcode (first 4 chars)
 *   • IE  → Eircode routing key + area name
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (v) => {
  if (!v) return '';
  const d = typeof v === 'string' ? new Date(v) : v;
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const NearbySoldPrices = ({ property }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!property) return;
    const loc = property.location || {};
    const country = loc.country || 'Ireland';
    const params = new URLSearchParams({ country, limit: '10' });
    const uk = /United Kingdom|UK|GB/i.test(country);
    if (uk) {
      if (loc.postcode) params.set('postcode', loc.postcode);
      else return;
    } else {
      const rk = property.eircode || loc.eircode;
      if (rk) params.set('eircode', rk);
      if (loc.county) params.set('county', loc.county);
      if (loc.area) params.set('area', loc.area);
    }
    setLoading(true);
    fetch(`${BACKEND_URL}/api/sold-prices/nearby?${params.toString()}`)
      .then((r) => r.ok ? r.json() : { items: [] })
      .then(setData)
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  }, [property]);

  if (!property || (data && data.items.length === 0)) return null;

  return (
    <div className="mt-4 rounded-lg ring-1 ring-gray-200 bg-white" data-testid="nearby-sold-prices">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#7042cc]" />
          <h3 className="text-sm font-bold text-gray-900">Recent sales nearby</h3>
        </div>
        {data?.median != null && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Median</div>
            <div className="text-sm font-bold text-gray-900">
              {data.items?.[0]?.currency || '€'}{Math.round(data.median).toLocaleString('en')}
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 px-4 py-6 text-xs text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Fetching official sold-price data…
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {(data?.items || []).map((it, i) => (
            <li key={i} className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center text-sm">
              <div className="col-span-7 text-gray-800 truncate" title={it.address}>{it.address}</div>
              <div className="col-span-2 text-xs text-gray-500">{fmtDate(it.date)}</div>
              <div className="col-span-3 text-right font-mono font-semibold text-gray-900">
                {it.currency || '€'}{it.price != null ? Math.round(it.price).toLocaleString('en') : '—'}
              </div>
            </li>
          ))}
        </ul>
      )}
      {data?.source && (
        <div className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100">
          Source: {data.source}. Public official data — not adjusted for property type or size.
        </div>
      )}
    </div>
  );
};

export default NearbySoldPrices;
